import React, { useEffect, useState } from "react";
import { Bot, Users, Zap, HelpCircle, TrendingUp, Percent } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import Loading from "./Loading";

const AXIS = { stroke: "#67e8f9", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };
const TOOLTIP_STYLE = {
  background: "#000",
  border: "1px solid #22d3ee",
  color: "#e6f7ff",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: "11px",
  padding: "6px 10px",
};

function StatCell({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className={`border p-3 text-center ${accent ? "border-[color:var(--sat-cyan)] sat-box-glow" : "border-[color:var(--sat-border)]"}`} data-testid={testid}>
      <div className="flex items-center justify-center gap-1 mb-1">
        <Icon size={11} className="sat-cyan" />
        <div className="sat-label sat-cyan">{label}</div>
      </div>
      <div className="font-mono font-bold text-2xl sat-cyan sat-glow">{value}</div>
    </div>
  );
}

/**
 * R2-A2 Analytics — owner-admin dashboard rendered inside AdminPage under the
 * 'R2-A2 ANALYTICS' tab. Pulls aggregated data from /api/admin/droid/analytics.
 */
export default function DroidAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/admin/droid/analytics")
      .then(r => { if (alive) { setData(r.data); setErr(null); } })
      .catch(e => alive && setErr(e?.response?.data?.detail || "Failed to load analytics"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) return <Loading message="AGGREGATING R2-A2 TELEMETRY" />;
  if (err || !data) return <div className="sat-label text-red-400 py-8" data-testid="droid-analytics-error">{err || "NO DATA"}</div>;

  const t = data.totals || {};

  return (
    <div data-testid="droid-analytics" className="space-y-6">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="droid-analytics-totals">
        <StatCell icon={Bot} label="TRANSMISSIONS" value={t.transmissions ?? "—"} testid="droid-total-transmissions" accent />
        <StatCell icon={Users} label="UNIQUE PILOTS" value={t.unique_pilots ?? "—"} testid="droid-total-pilots" />
        <StatCell icon={Percent} label="AVG / PILOT" value={t.avg_per_pilot ?? "—"} testid="droid-total-avg" />
        <StatCell icon={HelpCircle} label="UNKNOWN QUERIES" value={t.unknown_transmissions ?? "—"} testid="droid-total-unknown" />
      </div>

      {/* Daily trend */}
      <section className="border border-[color:var(--sat-border-bright)] p-4" data-testid="droid-analytics-daily">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={12} className="sat-cyan" />
          <div className="sat-label sat-cyan">TRANSMISSIONS · LAST 30 DAYS</div>
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily || []}>
              <CartesianGrid stroke="#164e63" strokeDasharray="2 4" />
              <XAxis dataKey="date" {...AXIS} tickFormatter={(v) => v.slice(5)} />
              <YAxis {...AXIS} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="count" stroke="#22d3ee" strokeWidth={2} dot={{ fill: "#22d3ee", r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Top keywords */}
      <section className="border border-[color:var(--sat-border-bright)] p-4" data-testid="droid-analytics-keywords">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={12} className="sat-cyan" />
          <div className="sat-label sat-cyan">MOST-ASKED KEYWORDS</div>
        </div>
        {(data.top_keywords || []).length === 0 ? (
          <div className="sat-label text-[color:var(--sat-text-dim)]">NO KEYWORD MATCHES YET</div>
        ) : (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.top_keywords} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="#164e63" strokeDasharray="2 4" />
                <XAxis type="number" {...AXIS} allowDecimals={false} />
                <YAxis dataKey="keyword" type="category" width={120} {...AXIS} interval={0} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v, _n, entry) => [`${v} (${entry.payload.share_pct}%)`, "count"]}
                />
                <Bar dataKey="count" fill="#67e8f9" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Two-column: Top pilots + Unknown samples */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top pilots */}
        <section className="border border-[color:var(--sat-border-bright)] p-4" data-testid="droid-analytics-pilots">
          <div className="flex items-center gap-2 mb-3">
            <Users size={12} className="sat-cyan" />
            <div className="sat-label sat-cyan">TOP PILOTS BY VOLUME</div>
          </div>
          {(data.top_pilots || []).length === 0 ? (
            <div className="sat-label text-[color:var(--sat-text-dim)]">NO PILOTS YET</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[color:var(--sat-border)]">
                  <th className="text-left sat-label py-2">PILOT</th>
                  <th className="text-right sat-label py-2">COUNT</th>
                  <th className="text-right sat-label py-2 hidden sm:table-cell">LAST SEEN</th>
                </tr>
              </thead>
              <tbody>
                {data.top_pilots.map((p, i) => (
                  <tr key={p.user_id} className="border-b border-[color:var(--sat-border)]" data-testid={`droid-pilot-row-${i}`}>
                    <td className="py-2 font-mono text-[11px] text-[color:var(--sat-text-dim)]">{p.email}</td>
                    <td className="py-2 text-right font-mono sat-cyan">{p.count}</td>
                    <td className="py-2 text-right font-mono text-[10px] text-[color:var(--sat-text-dim)] hidden sm:table-cell">
                      {p.last_seen ? p.last_seen.slice(0, 10) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Unknown-term samples */}
        <section className="border border-[color:var(--sat-border-bright)] p-4" data-testid="droid-analytics-unknown">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle size={12} className="sat-cyan" />
            <div className="sat-label sat-cyan">CONTENT GAPS · RECENT UNKNOWN QUERIES</div>
          </div>
          {(data.unknown_samples || []).length === 0 ? (
            <div className="sat-label text-[color:var(--sat-text-dim)]">R2-A2 MATCHED EVERY QUERY 🎯</div>
          ) : (
            <ul className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="droid-unknown-list">
              {data.unknown_samples.map((s, i) => (
                <li key={i} className="border border-[color:var(--sat-border)] p-2" data-testid={`droid-unknown-${i}`}>
                  <div className="font-mono text-[12px] text-[color:var(--sat-text)] leading-snug">"{s.message}"</div>
                  <div className="flex justify-between mt-1">
                    <span className="font-mono text-[9px] text-[color:var(--sat-text-dim)]">{s.email}</span>
                    <span className="font-mono text-[9px] text-[color:var(--sat-text-dim)]">
                      {s.created_at ? s.created_at.slice(0, 10) : "—"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
